import {
	type InvestmentBulkBulkStaticConfigurationControllerV1Api,
	type InvestmentBulkEnhancementConfigurationControllerV4Api,
	type InvestmentCreationConfigurationControllerV4Api,
	type InvestmentDraftConfigurationControllerV4Api,
	type InvestmentEnhancementConfigurationControllerV4Api,
	type MarketScenario,
	type MarketViewListEntry,
	type MarketViewSettings,
	MarketScenarioIds,
	MarketViewSettingsActiveMarketViewTypesEnum,
	MarketViewType,
} from "@eu.mdotm.api/sphere";
import {
	type ModalProps,
	type TableColumn,
	AutoSortTable,
	Dialog,
	DialogFooter,
	Icon,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { TextInput, Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { builtInSort, noop } from "@mdotm/shadcn/legacy/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EditPortfolioV4Props } from "../EditPortfolio.shared-types";
import { StepBase } from "../StepBase";
import { useStepSync } from "../hooks";
import { responseToStepMapper } from "../requests";
import { responseToStepMapper as responseToStepMapperMulti } from "../requests-multi";

import { reportPlatformError } from "$root/api/error-reporting";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { MarketViewPositioning } from "$root/functional-areas/market-view/MarketViewPositioning";
import { MarketViewProbabilities } from "$root/functional-areas/market-view/MarketViewProbabilities";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { Switch, adaptAnimatedNodeProvider, overrideClassName, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { applyDeltaToSelectedMarketView, removeDeltaFromSelectedMarketView } from "../../MarketView/utilsV2";
import type { EditPortfolioMultiV4Props } from "../EditPortfolioMulti.shared-types";
import { type ConstraintAdder, AddConstraintSection } from "../constraints/AddConstraintSection";
import type { EditPortfolioV4StepPayloadMap, StepPropsFor } from "../shared";
import type { StepMultiPropsFor } from "../shared-multi";

export function getMarketViewStepData(
	createApi: Omit<InvestmentCreationConfigurationControllerV4Api, "basePath" | "axios">,
	enhanceApi: Omit<InvestmentEnhancementConfigurationControllerV4Api, "basePath" | "axios">,
	draftApi: Omit<InvestmentDraftConfigurationControllerV4Api, "basePath" | "axios">,
	area: EditPortfolioV4Props["area"],
): Promise<EditPortfolioV4StepPayloadMap["marketView"]> {
	return (
		!area.portfolioUid
			? createApi.getCreationConfigurationMarketView()
			: area.name === "draft"
				? draftApi.getDraftConfigurationMarketView(area.portfolioUid)
				: enhanceApi.getEnhancementConfigurationMarketView(area.portfolioUid)
	).then(({ data }) => responseToStepMapper.marketView(data, area));
}

export function getBulkMarketViewStepData(
	bulkApi: Omit<InvestmentBulkEnhancementConfigurationControllerV4Api, "basePath" | "axios">,
	bulkSettingsApi: Omit<InvestmentBulkBulkStaticConfigurationControllerV1Api, "basePath" | "axios">,
	area: EditPortfolioMultiV4Props["area"],
): Promise<EditPortfolioV4StepPayloadMap["marketView"]> {
	return (
		area.name === "create-bulk-proposal"
			? bulkApi.getEnhancementConfigurationMarketView1(area.proposalUuid)
			: bulkSettingsApi.getEnhancementConfigurationMarketView2(area.bulkUid)
	).then(({ data }) => responseToStepMapperMulti.marketView(data, area));
}

/**
 * ID associated with a custom market view created on the step 06
 */
const customMarketViewId = "CUSTOM_PORTFOLIO_MARKET_VIEW";

/**
 * ID associated with a custom market view  created on the step 06 abd used to create a portfolio or a portfolio proposal.
 */
const currentMarketViewId = "CURRENT_PORTFOLIO";

export function Step06_MarketView({
	context,
	stepMetadata,
	stepData,
	onStepDataChange,
	onStepError,
	toggleDirty,
}: StepPropsFor<"marketView"> | StepMultiPropsFor<"marketView">): React.JSX.Element {
	const [item, setItem] = useState<{
		id: MarketViewType | null;
		isCustom?: boolean;
		action?: MarketViewSettingsActiveMarketViewTypesEnum | "load";
		name?: string;
	} | null>(null);
	const [isDirty, setIsDirty] = useState(false);

	const { area } = context;
	const edit = area.editable && area.edit;

	const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(null);
	const [marketScenario, setMarketScenario] = useState<MarketScenario | null>(null);
	const [originalScenario, setOriginalScenario] = useState<MarketScenario | null>(null);

	useEffect(() => {
		noop(currentScenarioId, marketScenario); // track
	}, [currentScenarioId, marketScenario]);

	const selectableMarketViewQuery = portfolioRepository.wizardSelectableMarketViews.useQuery(context.area);
	const queryMarketViewSettings = marketViewRepository.marketViewSettings.useQuery();
	const queryMarketViewAssetClassAliases = marketViewRepository.assetClassAliases.useQuery();
	const queryDefaultScenario = marketViewRepository.scenario.useQuery({
		scenarioId: MarketScenarioIds.SphereForecastPositioningOneMonth,
		investmentUuid: undefined,
		custom: false,
		useEnhancement: false,
	});

	const { data: marketViewSettings } = queryMarketViewSettings;
	const { data: marketViewAssetClassAliases } = queryMarketViewAssetClassAliases;
	const { data: defaultScenario } = queryDefaultScenario;

	// <Updates for calculating>
	useEffect(() => {
		setCurrentScenarioId(stepData.scenarioIdentifier ?? null);
	}, [stepData.scenarioIdentifier]);

	useEffect(() => {
		if (stepData.scenarioData) {
			const regimes = {
				lower: stepData.scenarioData?.regimeUserProbability?.a ?? 0,
				upper:
					(stepData.scenarioData?.regimeUserProbability?.a ?? 0) +
					(stepData.scenarioData?.regimeUserProbability?.b ?? 0),
			};

			const appliedAssetClasses = {
				...stepData.scenarioData,
				flexibleExpectedReturnsVolatility: {
					assetClasses: applyDeltaToSelectedMarketView(
						{
							a: regimes.lower,
							b: regimes.upper - regimes.lower,
							c: 100 - regimes.upper,
						},
						stepData.scenarioData.flexibleExpectedReturnsVolatility?.assetClasses,
					), // indicators.regimeUserProbability!
				},
			} satisfies MarketScenario;

			setMarketScenario(appliedAssetClasses);
			setOriginalScenario(stepData.scenarioData);
		}
	}, [stepData.scenarioData]);

	useEffect(() => {
		setItem({
			id: stepData.scenarioData?.marketViewType ?? null,
		});
	}, [stepData.scenarioData]);
	// </Updates for calculating>

	const isCalculating = stepMetadata.processing;
	const hasCustomMvTemplate = useMemo(
		() => marketViewSettings?.activeMarketViewTypes?.includes(MarketViewSettingsActiveMarketViewTypesEnum.Custom),
		[marketViewSettings?.activeMarketViewTypes],
	);

	const constraintAdders = useMemo<ConstraintAdder<MarketViewSettingsActiveMarketViewTypesEnum | "load">[]>(
		() => [
			{
				maxOfType: 1,
				label: "Load Market view",
				type: "load",
				disabled: isCalculating || item?.id !== null || selectableMarketViewQuery.isLoading,
				limitTooltip: selectableMarketViewQuery.isLoading
					? "Sphere is retrieving the market view configuration"
					: "You have already set a MarketView",
			},
			...(marketViewSettings?.activeMarketViewTypes?.includes(
				MarketViewSettingsActiveMarketViewTypesEnum.ExpectedReturnsVolatility,
			)
				? [
						{
							maxOfType: 1,
							label: "Expected returns",
							type: MarketViewSettingsActiveMarketViewTypesEnum.ExpectedReturnsVolatility,
							disabled: selectableMarketViewQuery.isLoading || isCalculating || item?.id !== null,
							limitTooltip: selectableMarketViewQuery.isLoading
								? "Sphere is retrieving the market view configuration"
								: "You have already set a MarketView",
						},
					]
				: []),
			...(marketViewSettings?.activeMarketViewTypes?.includes(
				MarketViewSettingsActiveMarketViewTypesEnum.PositioningIndicators,
			)
				? [
						{
							maxOfType: 1,
							label: "Under/over weight",
							type: MarketViewSettingsActiveMarketViewTypesEnum.PositioningIndicators,
							disabled: selectableMarketViewQuery.isLoading || isCalculating || item?.id !== null,
							limitTooltip: selectableMarketViewQuery.isLoading
								? "Sphere is retrieving the market view configuration"
								: "You have already set a MarketView",
						},
					]
				: []),
			...(hasCustomMvTemplate &&
			marketViewSettings?.customMarketViewType &&
			(selectableMarketViewQuery.data?.customDefaultScenarios?.length ?? 0) > 0
				? [
						{
							maxOfType: 1,
							label: marketViewSettings?.customMarketViewName ?? "Custom market view",
							type: MarketViewSettingsActiveMarketViewTypesEnum.Custom,
							disabled: selectableMarketViewQuery.isLoading || isCalculating || item?.id !== null,
							limitTooltip: selectableMarketViewQuery.isLoading
								? "Sphere is retrieving the market view configuration"
								: "You have already set a MarketView",
						},
					]
				: []),
		],
		[
			hasCustomMvTemplate,
			isCalculating,
			item?.id,
			marketViewSettings?.activeMarketViewTypes,
			marketViewSettings?.customMarketViewName,
			marketViewSettings?.customMarketViewType,
			selectableMarketViewQuery.data?.customDefaultScenarios?.length,
			selectableMarketViewQuery.isLoading,
		],
	);

	const isSubmitDisabled = useMemo(() => isCalculating, [isCalculating]);

	useStepSync({ reset: (prevData) => prevData, toggleDirty, isDirty, stepData });
	const stepIsCurrentlyProcessingErrorMessage = "cannot submit the market view step in calculating status";
	const handleSubmit = useCallback(async () => {
		try {
			if (isSubmitDisabled) {
				throw new Error(stepIsCurrentlyProcessingErrorMessage);
			}

			if (!originalScenario || !marketScenario || !currentScenarioId) {
				onStepDataChange(
					await context.saveHandlers.marketView({
						scenarioData: undefined,
						scenarioIdentifier: undefined,
						custom: undefined,
						name: undefined,
					}),
					{ skipMetadataUpdate: true, persist: true },
				);
				setIsDirty(false);

				return;
			}

			if (
				context.area.name === "create" ||
				context.area.name === "enhance" ||
				context.area.name === "draft" ||
				context.area.name === "create-bulk-proposal"
			) {
				const { flexibleExpectedReturnsVolatility } = removeDeltaFromSelectedMarketView(
					marketScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
					originalScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
					marketScenario.regimeUserProbability!,
				);

				onStepDataChange(
					await context.saveHandlers.marketView({
						scenarioData:
							item?.id === "POSITIONING_INDICATORS"
								? marketScenario
								: {
										...marketScenario,
										flexibleExpectedReturnsVolatility: { assetClasses: flexibleExpectedReturnsVolatility },
									},
						scenarioIdentifier: currentScenarioId,
						custom: item?.isCustom,
						name: item?.name,
					}),
					{ skipMetadataUpdate: true, persist: true },
				);
				setIsDirty(false);

				return;
			}

			if (!originalScenario || !marketScenario || !currentScenarioId) {
				onStepDataChange(
					await context.saveHandlers.marketView({
						scenarioData: {
							assetClasses: undefined,
							editable: undefined,
							flexibleExpectedReturnsVolatility: undefined,
							forecastHorizon: undefined,
							historicalInvestmentHorizon: undefined,
							marketViewType: undefined,
							positioningIndicators: undefined,
							regimeProbabilities: undefined,
							regimeUserProbability: undefined,
							yield: undefined,
						},
						scenarioIdentifier: undefined,
						custom: undefined,
						name: undefined,
					}),
					{ skipMetadataUpdate: true, persist: true },
				);
				setIsDirty(false);
			} else {
				const { flexibleExpectedReturnsVolatility } = removeDeltaFromSelectedMarketView(
					marketScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
					originalScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
					marketScenario.regimeUserProbability!,
				);
				onStepDataChange(
					await context.saveHandlers.marketView({
						scenarioData:
							item?.id === "POSITIONING_INDICATORS"
								? marketScenario
								: {
										...marketScenario,
										flexibleExpectedReturnsVolatility: { assetClasses: flexibleExpectedReturnsVolatility },
									},
						scenarioIdentifier: currentScenarioId,
						custom: item?.isCustom,
						name: item?.name,
					}),
					{ skipMetadataUpdate: true, persist: true },
				);
				setIsDirty(false);
			}
		} catch (err) {
			if (err instanceof Error && err.message === stepIsCurrentlyProcessingErrorMessage) {
				platformToast({
					children: "Market View step is currently processing, wait before submit step",
					severity: "info",
					icon: "Portfolio",
				});
				return;
			}

			onStepError();
			reportPlatformError(err, "ERROR", "portfolio-creation", {
				message: "Save market view step",
				payload: JSON.stringify(marketScenario),
			});
			throw err;
		}
	}, [
		isSubmitDisabled,
		originalScenario,
		marketScenario,
		currentScenarioId,
		context.area.name,
		context.saveHandlers,
		onStepDataChange,
		item?.id,
		item?.isCustom,
		item?.name,
		onStepError,
	]);

	useEffect(() => {
		context.submittersRef.current.marketView = handleSubmit;
	});

	const { formatDate } = useLocaleFormatters();

	return (
		<StepBase title="Market View" optional={stepMetadata.optional} onSubmitAsync={handleSubmit}>
			<p className="mb-4" data-qualifier="portfolioWizard/marketView/hint">
				In this step, you can tailor your portfolio to align with a specific market outlook. Load an existing market
				view, choose from Sphere&lsquo;s view, or customize it with your insights.
			</p>
			{(context.area.name === "create" ||
				context.area.name === "enhance" ||
				context.area.name === "create-bulk-proposal" ||
				edit) && (
				<AddConstraintSection
					constraintAdders={constraintAdders}
					constraintList={[]}
					onAdd={(type) => {
						if (type === "load") {
							spawn<string>(
								adaptAnimatedNodeProvider(({ resolve, show, onHidden, reject }) => (
									<LoadMarketViewModal
										userScenarios={selectableMarketViewQuery.data?.userScenarios ?? []}
										onSubmit={resolve}
										onClose={() => reject("user cancelled")}
										show={show}
										onAnimationStateChange={(state) => state === "hidden" && onHidden()}
										marketViewSetting={marketViewSettings}
									/>
								)),
							)
								.promise.then((customScenarioId) => {
									const customScenario = selectableMarketViewQuery.data?.userScenarios?.find(
										(x) => x.uuid === customScenarioId,
									);
									if (!customScenario) {
										throw new Error("invalid scenario id");
									}

									setItem({
										id: customScenario.type!,
										isCustom: customScenario.custom,
										action: "load",
										name: customScenario?.name,
									});
									setCurrentScenarioId(customScenarioId);
									setIsDirty(true);
								}, noop)
								.catch(console.warn);
						}

						if (type === "POSITIONING_INDICATORS") {
							const defaultScenarioId = selectableMarketViewQuery.data?.positioningDefaultScenarios?.[0].id;
							setItem({ id: type, isCustom: false, action: "POSITIONING_INDICATORS" });

							if (defaultScenarioId) {
								setCurrentScenarioId(defaultScenarioId);
							}
							setIsDirty(true);
						}

						if (type === "EXPECTED_RETURNS_VOLATILITY") {
							const defaultScenarioId = selectableMarketViewQuery.data?.returnsVolatilityDefaultScenarios?.[0].id;
							setItem({ id: type, isCustom: false, action: "EXPECTED_RETURNS_VOLATILITY" });
							if (defaultScenarioId) {
								setCurrentScenarioId(defaultScenarioId);
							}
							setIsDirty(true);
						}

						if (type === "CUSTOM") {
							const defaultScenarioId = selectableMarketViewQuery.data?.customDefaultScenarios?.[0].id;
							setItem({
								id: marketViewSettings?.customMarketViewType ?? null,
								isCustom: true,
								action: "CUSTOM",
							});
							if (defaultScenarioId) {
								setCurrentScenarioId(defaultScenarioId);
							}
							setIsDirty(true);
						}
					}}
					customLabel="Select how you want to indicate your Market View"
				/>
			)}
			{!item?.id ||
			((context.area.name === "settings-current" || context.area.name === "settings-enhanced" ? !edit : false) &&
				!currentScenarioId) ? (
				<div className="flex-1 min-h-0 flex flex-col">
					<IconWalls.ConstraintNotSet constraintName="market view" />
				</div>
			) : (
				<div className="relative">
					{(context.area.name === "create" ||
						context.area.name === "enhance" ||
						context.area.name === "create-bulk-proposal" ||
						edit) && (
						<button
							data-qualifier="portfolioWizard/marketView/delete"
							type="button"
							className="absolute top-5 right-2 z-10"
							onClick={(e) => {
								setIsDirty(true);
								setItem({ id: null, isCustom: false });
								setMarketScenario(null);
								setCurrentScenarioId(null);
								e.preventDefault();
								e.stopPropagation();
							}}
						>
							<Icon icon="Delete" size={21} color={themeCSSVars.Button_bg_primary} />
						</button>
					)}
					{item?.id && (
						<ReactQueryWrapperBase query={selectableMarketViewQuery}>
							{(selectableMarketViews) => (
								<Switch
									case={item.id!}
									match={{
										[MarketViewType.PositioningIndicators]: () => {
											const customScenario = selectableMarketViews?.userScenarios?.find(
												(x) => x.uuid === currentScenarioId,
											);

											return (
												<MarketViewPositioning
													className="py-0!"
													mode={(() => {
														if (
															context.area.name === "create" ||
															context.area.name === "draft" ||
															context.area.name === "enhance" ||
															context.area.name === "create-bulk-proposal" ||
															context.area.name === "edit-bulk-settings"
														) {
															if (
																marketScenario?.editable &&
																customScenario === undefined &&
																currentScenarioId !== currentMarketViewId
															) {
																return "edit";
															}
														}

														if (context.area.name === "settings-current") {
															if (edit && marketScenario?.editable && currentScenarioId !== customMarketViewId) {
																return "edit";
															}
														}

														return "view";
													})()}
													marketScenarioProvider={async (scenarioId) => {
														if (scenarioId === stepData.scenarioIdentifier && stepData.scenarioData) {
															return stepData.scenarioData;
														}

														let loadedScenario = await portfolioRepository.wizardMarketViewConfiguration.fetchQuery(
															context.area,
															{
																scenarioId,
																custom: item.isCustom,
															},
														);

														if (currentScenarioId !== currentMarketViewId && item.action !== "load") {
															loadedScenario = {
																...loadedScenario,
																positioningIndicators: {
																	...loadedScenario.positioningIndicators,
																	positioningIndicators: item.isCustom
																		? loadedScenario.positioningIndicators?.positioningIndicators
																		: loadedScenario.positioningIndicators?.positioningIndicators?.map((indicator) => {
																				const mapped = {
																					...indicator,
																					mdotmCommentary:
																						defaultScenario?.positioningIndicators?.positioningIndicators?.find(
																							(x) => x.microAssetClass === indicator.microAssetClass,
																						)?.commentary,
																					mdotmPositioning:
																						defaultScenario?.positioningIndicators?.positioningIndicators?.find(
																							(x) => x.microAssetClass === indicator.microAssetClass,
																						)?.defaultPositioning,
																				};

																				if (currentScenarioId !== MarketScenarioIds.SphereForecastPositioningOneMonth) {
																					mapped.commentary = "";
																				}

																				if (
																					currentScenarioId !== MarketScenarioIds.UserForecastPositioningOneMonth &&
																					currentScenarioId !== MarketScenarioIds.SphereForecastPositioningOneMonth
																				) {
																					mapped.mdotmCommentary = undefined;
																					mapped.mdotmPositioning = undefined;
																				}

																				return mapped;
																			}),
																},
															};
														}

														setOriginalScenario(loadedScenario);
														return loadedScenario;
													}}
													marketScenario={marketScenario}
													onMarketScenarioChange={(scenario) => {
														setMarketScenario(scenario);
													}}
													scenarioOptions={
														customScenario
															? [{ label: customScenario.name ?? "-", value: customScenario.uuid ?? "-" }]
															: item.isCustom
																? (selectableMarketViews.customDefaultScenarios?.map((x, i) => ({
																		label: x.label ?? `Untitled ${i}`,
																		value: x.id ?? `untitled_${i}`,
																	})) ?? [])
																: (selectableMarketViews?.positioningDefaultScenarios
																		?.map((x, i) => ({
																			label: x.label ?? `Untitled ${i}`,
																			value: x.id ?? `untitled_${i}`,
																		}))
																		.concat(
																			stepData.scenarioData?.marketViewType === "POSITIONING_INDICATORS" &&
																				stepData.scenarioIdentifier === customMarketViewId
																				? [
																						{
																							label: `Market view ${formatDate(
																								stepData.scenarioData?.historicalInvestmentHorizon?.from,
																							)}`,
																							value: stepData.scenarioIdentifier,
																						},
																					]
																				: [],
																		) ?? [])
													}
													selectedScenarioId={currentScenarioId}
													onSelectedScenarioChange={setCurrentScenarioId}
													canReadCommentary={marketViewSettings?.commentaryEditorVisible}
													alias={marketViewAssetClassAliases?.assetClassAliases}
													marketViewSettings={item.isCustom ? marketViewSettings : undefined}
													hideOutlookToopltip={
														item.isCustom ||
														!(
															currentScenarioId === MarketScenarioIds.UserForecastPositioningOneMonth ||
															currentScenarioId === MarketScenarioIds.SphereForecastPositioningOneMonth
														)
													}
												/>
											);
										},
										[MarketViewType.ExpectedReturnsVolatility]: () => {
											const customScenario = selectableMarketViews?.userScenarios?.find(
												(x) => x.uuid === currentScenarioId,
											);
											return (
												<MarketViewProbabilities
													mode={(() => {
														if (
															context.area.name === "create" ||
															context.area.name === "draft" ||
															context.area.name === "enhance" ||
															context.area.name === "create-bulk-proposal" ||
															context.area.name === "edit-bulk-settings"
														) {
															if (
																marketScenario?.editable &&
																customScenario === undefined &&
																currentScenarioId !== currentMarketViewId
															) {
																return "edit";
															}
														}

														if (context.area.name === "settings-current") {
															if (edit && marketScenario?.editable && currentScenarioId !== customMarketViewId) {
																return "edit";
															}
														}

														return "view";
													})()}
													marketScenarioProvider={async (scenarioId) => {
														if (scenarioId === stepData.scenarioIdentifier && stepData.scenarioData) {
															return stepData.scenarioData;
														}

														const marketViewConfiguration =
															await portfolioRepository.wizardMarketViewConfiguration.fetchQuery(context.area, {
																scenarioId,
																custom: item.isCustom,
															});

														setOriginalScenario(marketViewConfiguration);
														return marketViewConfiguration;
													}}
													marketScenario={marketScenario}
													onMarketScenarioChange={setMarketScenario}
													scenarioOptions={
														customScenario
															? [{ label: customScenario.name ?? "-", value: customScenario.uuid ?? "-" }]
															: item.isCustom
																? (selectableMarketViews.customDefaultScenarios?.map((x, i) => ({
																		label: x.label ?? `Untitled ${i}`,
																		value: x.id ?? `untitled_${i}`,
																	})) ?? [])
																: (selectableMarketViews?.returnsVolatilityDefaultScenarios
																		?.map((x, i) => ({
																			label: x.label ?? `Untitled ${i}`,
																			value: x.id ?? `untitled_${i}`,
																		}))
																		.concat(
																			stepData.scenarioData?.marketViewType === "EXPECTED_RETURNS_VOLATILITY" &&
																				stepData.scenarioIdentifier === customMarketViewId
																				? [
																						{
																							label: `Market view ${formatDate(
																								stepData.scenarioData?.historicalInvestmentHorizon?.from,
																							)}`,
																							value: stepData.scenarioIdentifier,
																						},
																					]
																				: [],
																		) ?? [])
													}
													selectedScenarioId={currentScenarioId}
													onSelectedScenarioChange={setCurrentScenarioId}
													canReadCommentary={marketViewSettings?.commentaryEditorVisible}
													alias={marketViewAssetClassAliases?.assetClassAliases}
													marketViewSettings={item.isCustom ? marketViewSettings : undefined}
													hidePositioning={
														selectableMarketViews?.userScenarios?.some((x) => x.uuid === currentScenarioId) ||
														currentScenarioId === customMarketViewId
													}
												/>
											);
										},
									}}
								/>
							)}
						</ReactQueryWrapperBase>
					)}
				</div>
			)}
		</StepBase>
	);
}

function LoadMarketViewModal(props: {
	userScenarios: MarketViewListEntry[];
	onSubmit?(uuid: string): void;
	show: boolean;
	onClose: () => void;
	onAnimationStateChange?: ModalProps["onAnimationStateChange"];
	marketViewSetting?: MarketViewSettings;
}) {
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();
	const searchable = useSearchable({
		collection: props.userScenarios,
		extractFn: (row) => Object.values(row).join(" "),
	});

	const {
		column: checkBoxColumn,
		rowClassName,
		toggle,
		multiSelectCtx,
	} = useSelectableTableColumn({
		rows: searchable.filtered,
		selectBy: ({ uuid }) => uuid ?? "",
		mode: "radio",
	});

	const columns = useMemo<TableColumn<MarketViewListEntry>[]>(
		() => [
			checkBoxColumn,
			{
				name: "name",
				header: t("TABLE.HEADERS.NAME"),
				contentTextType: "Body/M/Bold",
				content: (row) => row.name,
			},
			{
				name: "type",
				header: t("TABLE.HEADERS.TYPE"),
				content: (row) => {
					if (row.custom) {
						return props.marketViewSetting?.customMarketViewName ?? "-";
					}
					return row.type ? t(row.type) : "-";
				},
			},
			{
				name: "creationDate",
				header: t("TABLE.HEADERS.CREATION"),
				content: (row) => (row.creationDate ? formatDate(new Date(row.creationDate)) : "-"),
			},
			{
				name: "forecastHorizon",
				header: t("TABLE.HEADERS.FORECAST_HORIZON"),
				content: (row) => (row.forecastHorizon ? t(`INVESTMENT_HORIZON.${row.forecastHorizon}`) : "-"),
			},
			{
				name: "expiration",
				header: t("TABLE.HEADERS.EXPIRATION"),
				content: (row, cellProps) => {
					if (!row.endDate) {
						return "-";
					}
					const expirationDate = new Date(row.endDate);
					if (differenceInDays(new Date(), expirationDate) > 0) {
						return (
							<div
								className={overrideClassName(cellProps.className, "flex items-center justify-start")}
								style={cellProps.style}
							>
								<div
									className={`bg-[var(--palette-W50)] flex items-center gap-2 py-1 px-2 text-[var(--palette-W500)] rounded-full`}
								>
									<Icon icon="Icon-full-alert" color={themeCSSVars.palette_W500} size={16} />
									<p className="font-semibold">{formatDate(expirationDate)}</p>
								</div>
							</div>
						);
					}

					return formatDate(expirationDate);
				},
				sortFn: ({ endDate: a }, { endDate: b }) => {
					const rowA = new Date(a || "").getTime();
					const rowB = new Date(b || "").getTime();
					return builtInSort(rowA, rowB);
				},
			},
		],
		[checkBoxColumn, formatDate, props.marketViewSetting?.customMarketViewName, t],
	);

	return (
		<Dialog
			onAnimationStateChange={props.onAnimationStateChange}
			size="xxlarge"
			show={props.show}
			onClose={() => props.onClose()}
			onSubmitAsync={() => {
				props.onSubmit?.(multiSelectCtx.selection.first());
			}}
			header="Select existing market view"
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							data-qualifier="portfolioWizard/marketView/loadMarketView"
							disabled={multiSelectCtx.selection.size === 0}
						>
							Load MarketView
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={() => props.onClose()}>
							Cancel
						</Button>
					}
				/>
			}
		>
			<div className="mb-4">
				<TextInput
					data-qualifier="portfolioWizard/marketView/filterByName"
					placeholder="Filter by name"
					className="max-w-[480px]"
					value={searchable.query}
					onChangeText={searchable.setQuery}
				/>
			</div>
			<AutoSortTable
				data-qualifier="portfolioWizard/marketView/filteredEntries"
				rows={searchable.filtered}
				rowClassName={rowClassName}
				columns={columns}
				className="max-h-[410px]"
				onRowClick={({ uuid }) => toggle(uuid ?? "")}
				noDataText="no scenario available"
			/>
		</Dialog>
	);
}
